import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { openPrintWindow } from "@/utils/printHelper";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Printer, Edit, Calendar, Copy, FileText, CheckCircle2, XCircle, Clock, Box, Building2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ViewWorkOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cloning, setCloning] = useState(false);
  const [productImages, setProductImages] = useState([]);
  
  // Determine base path - use unified page for workorder
  const isWorkOrderPath = location.pathname.startsWith('/workorder');
  const basePath = isWorkOrderPath ? '/workorder' : '/admin';
  const savedPath = isWorkOrderPath ? '/workorder#saved' : '/admin/saved';

  useEffect(() => {
    fetchWorkOrder();
  }, [id]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/work-orders/${id}`);
      setWorkOrder(response.data);
      // Fetch product image URLs from product specifications.
      // Collect all available image URLs so we can render one QR code per image.
      const productId = response.data?.header?.product_id;
      if (productId) {
        try {
          const specsRes = await axios.get(`${API}/product-specifications`);
          const spec = specsRes.data.find(s => s.product_id === productId);
          if (spec) {
            const collected = [];
            const seen = new Set();
            const pushUrl = (url, filename) => {
              if (!url) return;
              if (seen.has(url)) return;
              seen.add(url);
              collected.push({ url, filename: filename || "" });
            };
            // Primary image first (if defined)
            pushUrl(spec.primary_image_url);
            // Legacy single image_link
            pushUrl(spec.image_link);
            // All entries in image_links
            (spec.image_links || []).forEach(img => {
              if (img && img.url) pushUrl(img.url, img.filename);
            });
            setProductImages(collected);
          }
        } catch (e) {
          console.error("Error fetching product spec for image:", e);
        }
      }
    } catch (error) {
      console.error("Error fetching work order:", error);
      toast.error("Failed to load work order");
      navigate(savedPath);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = document.querySelector('.print-only');
    if (!printContent) { window.print(); return; }
    const htmlContent = `<!DOCTYPE html><html><head><title>CPI-WorkStation</title>
      <style>${printContent.querySelector('style')?.textContent || ''}</style>
      </head><body>${printContent.querySelector('.wo-print-page')?.outerHTML || ''}
      <script>
        var imgs = document.querySelectorAll('img');
        var loaded = 0; var total = imgs.length;
        function triggerPrint() { setTimeout(function() { window.print(); }, 500); }
        if (total === 0) { triggerPrint(); } else {
          imgs.forEach(function(img) {
            if (img.complete) { loaded++; } else {
              img.onload = function() { loaded++; if (loaded >= total) triggerPrint(); };
              img.onerror = function() { loaded++; if (loaded >= total) triggerPrint(); };
            }
          });
          if (loaded >= total) triggerPrint();
          setTimeout(triggerPrint, 3000);
        }
      </` + `script></body></html>`;
    openPrintWindow(htmlContent);
  };

  const handleClone = async () => {
    if (!workOrder) return;
    
    try {
      setCloning(true);
      
      // Get the next work order number
      const allWOResponse = await axios.get(`${API}/work-orders`);
      const allWorkOrders = allWOResponse.data || [];
      
      // Find the highest WONO number
      let maxNumber = 0;
      allWorkOrders.forEach(wo => {
        const woNum = wo.header?.work_order_number || '';
        const match = woNum.match(/WONO-(\d+)/);
        if (match) {
          const num = parseInt(match[1], 10);
          if (num > maxNumber) maxNumber = num;
        }
      });
      
      const nextNumber = String(maxNumber + 1).padStart(3, '0');
      const newWONumber = `WONO-${nextNumber}`;
      
      // Navigate to creation page with clone data
      navigate(`${basePath}#creation`, {
        state: {
          cloneData: {
            ...workOrder,
            id: undefined, // Remove ID so it creates new
            header: {
              ...workOrder.header,
              work_order_number: newWONumber,
              work_order_creation_date: new Date().toISOString().split('T')[0],
            },
            status: 'available',
            completion_history: [],
          }
        }
      });
      
      toast.success(`Cloning to ${newWONumber}`);
    } catch (error) {
      console.error("Error cloning work order:", error);
      toast.error("Failed to clone work order");
    } finally {
      setCloning(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dark-theme min-h-screen p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-yellow-400 text-lg">Loading Work Order...</p>
        </div>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="admin-dark-theme min-h-screen p-8 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 text-xl">Work order not found</p>
          <Button onClick={() => navigate(savedPath)} className="mt-4">Go Back</Button>
        </div>
      </div>
    );
  }

  // Check which processes have actual meaningful data for print
  // Returns true ONLY if the process has data that should be displayed
  const hasProcessData = (processKey) => {
    const process = workOrder.processes?.[processKey];
    if (!process || typeof process !== 'object') return false;
    
    // Define the key fields that indicate actual data for each process
    const processKeyFields = {
      process1: ['flute_type', 'rows'], // Corrugation
      process2: ['cutting_size', 'sheet_counting', 'paper_counting', 'reels'], // Sheet Cutting (Legacy)
      process2a: ['cutting_size', 'sheet_counting', 'reels', 'reel_size'], // Sheet Cutting (New)
      process2b: ['cutting_size', 'paper_counting', 'reels'], // Paper Cutting (New)
      process3: ['no_of_ply', 'top_paper_type', 'sheet_counting'], // Sheet Pasting
      process4: ['board_counting', 'length', 'breadth', 'height'], // Slitter Scorer
      process5: ['printing', 'color', 'board_counting'], // Rotary Creasing
      process6: ['printing', 'color', 'board_counting'], // RS4 Printing
      process6a: ['length', 'breadth', 'height', 'board_counting'], // Eccentric Slotter (added Feb 2026)
      process7: ['item_name', 'die_id', 'counting'], // Flatbed Punching
      process8: ['die_id', 'printing', 'color', 'counting'], // Rotary Die Punching
      process9: ['calico_color', 'number_of_calico'], // Calico Pasting
      process10: ['boxes_in_bundle', 'stitching_type', 'total_bundles'], // Stitching
      process11: ['loading_type', 'vehicle_number'], // Loading
      process12: ['transport_type', 'destination'], // Transport
    };
    
    const keyFields = processKeyFields[processKey] || [];
    
    return keyFields.some(field => {
      const val = process[field];
      if (Array.isArray(val)) {
        // For arrays like rows/reels, check if they have items with actual data
        return val.length > 0 && val.some(item => 
          Object.values(item).some(v => v !== null && v !== undefined && v !== '' && v !== false)
        );
      }
      return val !== undefined && val !== null && val !== '';
    });
  };

  const processLabels = {
    process1: "P1 - Corrugation",
    process2: "P2 - Sheet Cutting (Legacy)",
    process2a: "P2A - Sheet Cutting",
    process2b: "P2B - Paper Cutting",
    process3: "P3 - Sheet Pasting",
    process4: "P4 - Thin Blade Slitter",
    process5: "P5 - 4 Bar Rotary",
    process6: "P6 - RS4 Printing",
    process6a: "P6A - Eccentric Slotter",
    process7: "P7 - Flat bed Punching",
    process8: "P8 - Rotary Die Punching",
    process9: "P9 - Calico Pasting",
    process10: "P10 - Stitching & Bundling",
    process11: "P11 - Loading/Unloading",
    process12: "P12 - Transport",
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500';
      case 'in_progress': return 'bg-blue-500/20 text-blue-400 border-blue-500';
      case 'delivered': return 'bg-purple-500/20 text-purple-400 border-purple-500';
      case 'rejected': return 'bg-red-500/20 text-red-400 border-red-500';
      default: return 'bg-yellow-500/20 text-yellow-400 border-yellow-500';
    }
  };

  const processFields = {
    process4: ['length', 'breadth', 'height', 'board_counting'],
    process5: ['printing', 'color', 'board_counting'],
    process6: ['length', 'breadth', 'height', 'printing', 'color', 'board_counting'],
    process7: ['item_name', 'die_id', 'counting'],
    process8: ['die_id', 'printing', 'color', 'counting'],
    process9: ['calico_color', 'number_of_calico'],
    process10: ['boxes_in_bundle', 'stitching_type', 'total_bundles'],
    process11: ['loading_type', 'vehicle_number', 'remarks'],
    process12: ['transport_type', 'destination', 'transport_remarks'],
  };

  const fieldLabels = {
    length: 'Length', breadth: 'Breadth', height: 'Height', board_counting: 'Board Counting',
    printing: 'Printing', color: 'Color', item_name: 'Item Name', die_id: 'Die ID',
    counting: 'Counting', calico_color: 'Calico Color', number_of_calico: 'Number of Calico',
    boxes_in_bundle: 'Boxes in Bundle', stitching_type: 'Stitching Type', total_bundles: 'Total Bundles',
    loading_type: 'Loading Type', vehicle_number: 'Vehicle Number', remarks: 'Remarks',
    transport_type: 'Transport Type', destination: 'Destination', transport_remarks: 'Transport Remarks',
  };

  const processColors = {
    process4: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-l-purple-500', badge: 'bg-purple-500' },
    process5: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-l-orange-500', badge: 'bg-orange-500' },
    process6: { bg: 'bg-pink-500/10', text: 'text-pink-400', border: 'border-l-pink-500', badge: 'bg-pink-500' },
    process7: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-l-cyan-500', badge: 'bg-cyan-500' },
    process8: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-l-red-500', badge: 'bg-red-500' },
    process9: { bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-l-indigo-500', badge: 'bg-indigo-500' },
    process10: { bg: 'bg-teal-500/10', text: 'text-teal-400', border: 'border-l-teal-500', badge: 'bg-teal-500' },
    process11: { bg: 'bg-lime-500/10', text: 'text-lime-400', border: 'border-l-lime-500', badge: 'bg-lime-500' },
    process12: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-l-amber-500', badge: 'bg-amber-500' },
  };

  return (
    <div className="admin-dark-theme min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 print:hidden">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate(savedPath)} variant="ghost" className="text-gray-400 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-yellow-400 flex items-center gap-3">
                <FileText className="h-8 w-8" />
                {workOrder.header?.work_order_number}
              </h1>
              <p className="text-gray-400 mt-1">{workOrder.header?.customer_name} - {workOrder.header?.item_name}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={handleClone} disabled={cloning} className="bg-green-600 hover:bg-green-700">
              <Copy className="h-4 w-4 mr-2" />
              {cloning ? 'Cloning...' : 'Clone'}
            </Button>
            <Button onClick={() => navigate(`${basePath}/edit/${id}`)} className="bg-blue-600 hover:bg-blue-700">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button onClick={handlePrint} className="bg-purple-600 hover:bg-purple-700">
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </div>
        </div>

        {/* Quick Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6 print:hidden">
          <div className="bg-black/40 border border-yellow-500/30 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400">Status</p>
            <span className={`px-2 py-1 rounded text-sm font-bold border ${getStatusColor(workOrder.status)}`}>
              {workOrder.status?.toUpperCase() || 'AVAILABLE'}
            </span>
          </div>
          <div className="bg-black/40 border border-yellow-500/30 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400">Quantity</p>
            <p className="text-xl font-bold text-yellow-400">{workOrder.header?.required_quantity || '-'}</p>
          </div>
          <div className="bg-black/40 border border-yellow-500/30 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400">Delivery Date</p>
            <p className="text-sm font-bold text-white">{workOrder.header?.delivery_date || '-'}</p>
          </div>
          <div className="bg-black/40 border border-yellow-500/30 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400">Reel Size</p>
            <p className="text-xl font-bold text-green-400">{workOrder.header?.reel_size || '-'}</p>
          </div>
          <div className="bg-black/40 border border-yellow-500/30 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400">Cutting Size</p>
            <p className="text-xl font-bold text-blue-400">{workOrder.header?.cutting_size || '-'}</p>
          </div>
          <div className="bg-black/40 border border-yellow-500/30 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400">PO Number</p>
            <p className="text-sm font-bold text-white">{workOrder.header?.po_number || '-'}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 print:hidden">
          {/* Left Column */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="bg-black/60 border-2 border-yellow-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-yellow-400 text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Order Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-black/40 p-3 rounded">
                    <p className="text-xs text-gray-400">Customer</p>
                    <p className="font-semibold text-white">{workOrder.header?.customer_name || '-'}</p>
                  </div>
                  <div className="bg-black/40 p-3 rounded">
                    <p className="text-xs text-gray-400">Item Name</p>
                    <p className="font-semibold text-white">{workOrder.header?.item_name || '-'}</p>
                  </div>
                  <div className="bg-black/40 p-3 rounded">
                    <p className="text-xs text-gray-400">Requested By</p>
                    <p className="font-semibold text-white">{workOrder.header?.requested_by || '-'}</p>
                  </div>
                  <div className="bg-black/40 p-3 rounded">
                    <p className="text-xs text-gray-400">Order Date</p>
                    <p className="font-semibold text-white">{workOrder.header?.order_received_date || '-'}</p>
                  </div>
                  <div className="bg-black/40 p-3 rounded col-span-2">
                    <p className="text-xs text-gray-400">WO Creation Date</p>
                    <p className="font-semibold text-white">{workOrder.header?.work_order_creation_date || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-black/60 border-2 border-blue-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-blue-400 text-lg flex items-center gap-2">
                  <Box className="h-5 w-5" />
                  Box Dimensions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-blue-500/10 p-3 rounded text-center">
                    <p className="text-xs text-gray-400">Length</p>
                    <p className="text-lg font-bold text-blue-400">{workOrder.header?.length || '-'}</p>
                    <p className="text-xs text-gray-500">{workOrder.header?.length_mm || '-'} mm</p>
                  </div>
                  <div className="bg-blue-500/10 p-3 rounded text-center">
                    <p className="text-xs text-gray-400">Breadth</p>
                    <p className="text-lg font-bold text-blue-400">{workOrder.header?.breadth || '-'}</p>
                    <p className="text-xs text-gray-500">{workOrder.header?.breadth_mm || '-'} mm</p>
                  </div>
                  <div className="bg-blue-500/10 p-3 rounded text-center">
                    <p className="text-xs text-gray-400">Height</p>
                    <p className="text-lg font-bold text-blue-400">{workOrder.header?.height || '-'}</p>
                    <p className="text-xs text-gray-500">{workOrder.header?.height_mm || '-'} mm</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-black/60 border-2 border-green-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-green-400 text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  Selected Processes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(processLabels).map(([key, label]) => {
                    const hasData = hasProcessData(key);
                    return (
                      <div key={key} className={`p-2 rounded text-xs font-medium flex items-center gap-1 ${hasData ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-gray-800/50 text-gray-500 border border-gray-700'}`}>
                        {hasData ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                        {label}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Process Details */}
          <div className="lg:col-span-2 space-y-4">
            {/* Process 1 */}
            {hasProcessData('process1') && (
              <Card className="bg-black/60 border-l-4 border-l-yellow-500 border-r border-t border-b border-gray-700">
                <CardHeader className="py-3">
                  <CardTitle className="text-yellow-400 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="bg-yellow-500 text-black px-2 py-0.5 rounded text-sm font-bold">P1</span>
                      Corrugation Machine
                    </span>
                    <span className="text-sm text-gray-400 flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {workOrder.processes?.process1?.date || '-'}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="mb-2 px-3 py-1 bg-yellow-500/10 rounded text-sm">
                    <span className="text-gray-400">Flute Type:</span>
                    <span className="text-yellow-400 font-bold ml-2">{workOrder.processes?.process1?.flute_type || '-'}</span>
                  </div>
                  {workOrder.processes?.process1?.rows?.length > 0 && (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-400 border-b border-gray-700">
                          <th className="text-left py-2">Base Reel ID</th>
                          <th className="text-right py-2">Weight</th>
                          <th className="text-left py-2 pl-4">Flute Reel ID</th>
                          <th className="text-right py-2">Weight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workOrder.processes.process1.rows.map((row, i) => (
                          <tr key={i} className="border-b border-gray-800">
                            <td className="py-2 text-yellow-400 font-mono">{row.base_reel_id || '-'}</td>
                            <td className="py-2 text-right text-white">{row.base_weight || '-'}</td>
                            <td className="py-2 pl-4 text-green-400 font-mono">{row.flute_reel_id || '-'}</td>
                            <td className="py-2 text-right text-white">{row.flute_weight || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {/* Reel Split Info - check both P1 (legacy) and P2A (current) */}
                  {(() => {
                    const splitData = workOrder.processes?.process2a?.reel_split || workOrder.processes?.process1?.reel_split;
                    if (!splitData?.has_split) return null;
                    return (
                      <div className="mt-3 p-3 rounded-lg border border-amber-500/40 bg-amber-500/5" data-testid="view-reel-split-info">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center bg-amber-500 text-black">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z" /></svg>
                          </div>
                          <span className="text-amber-400 font-bold text-sm">ROLL SPLIT</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div className="bg-black/30 p-2 rounded">
                            <p className="text-xs text-gray-400">Stock Roll</p>
                            <p className="font-bold text-white">{splitData.stock_reel_size}"</p>
                          </div>
                          <div className="bg-black/30 p-2 rounded">
                            <p className="text-xs text-gray-400">Item Uses</p>
                            <p className="font-bold text-white">{splitData.item_reel_size}"</p>
                          </div>
                          <div className="bg-black/30 p-2 rounded">
                            <p className="text-xs text-gray-400">Remaining</p>
                            <p className="font-bold text-amber-400">{splitData.remaining_size}"</p>
                          </div>
                        </div>
                        <div className="mt-2 text-sm">
                          <span className="text-gray-400">Handling: </span>
                          <span className="text-white font-medium">
                            {splitData.handling === "assign_existing_wo" && `Assigned to ${splitData.assigned_wo}`}
                            {splitData.handling === "new_draft_wo" && `New Draft WO: ${splitData.assigned_wo} (Data Pending)`}
                            {splitData.handling === "bit_roll_stock" && "Stored as Bit Roll in Stock Inventory"}
                            {!splitData.handling && "Not specified"}
                          </span>
                        </div>
                        {splitData.notes && (
                          <div className="mt-1 text-sm">
                            <span className="text-gray-400">Notes: </span>
                            <span className="text-gray-300">{splitData.notes}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Process 2 (Legacy) */}
            {hasProcessData('process2') && (
              <Card className="bg-black/60 border-l-4 border-l-blue-500 border-r border-t border-b border-gray-700">
                <CardHeader className="py-3">
                  <CardTitle className="text-blue-400 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="bg-blue-500 text-black px-2 py-0.5 rounded text-sm font-bold">P2</span>
                      Sheet / Paper Cutting (Legacy)
                    </span>
                    <span className="text-sm text-gray-400 flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {workOrder.processes?.process2?.date || '-'}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-3 gap-3">
                    {['reel_no', 'bit_no', 'reel_size', 'cutting_size', 'sheet_counting', 'paper_counting'].map(field => {
                      const val = workOrder.processes?.process2?.[field];
                      if (!val) return null;
                      return (
                        <div key={field} className="bg-blue-500/10 p-2 rounded">
                          <p className="text-xs text-gray-400 capitalize">{field.replace(/_/g, ' ')}</p>
                          <p className="font-bold text-white">{val}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Process 2A - Sheet Cutting */}
            {hasProcessData('process2a') && (
              <Card className="bg-black/60 border-l-4 border-l-cyan-500 border-r border-t border-b border-gray-700">
                <CardHeader className="py-3">
                  <CardTitle className="text-cyan-400 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="bg-cyan-500 text-black px-2 py-0.5 rounded text-sm font-bold">P2A</span>
                      Sheet Cutting Machine
                    </span>
                    <span className="text-sm text-gray-400 flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {workOrder.processes?.process2a?.date || '-'}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-3 gap-3">
                    {workOrder.processes?.process2a?.reel_size && (
                      <div className="bg-cyan-500/10 p-2 rounded">
                        <p className="text-xs text-gray-400">Reel Size</p>
                        <p className="font-bold text-white">{workOrder.processes.process2a.reel_size}</p>
                      </div>
                    )}
                    {workOrder.processes?.process2a?.cutting_size && (
                      <div className="bg-cyan-500/10 p-2 rounded">
                        <p className="text-xs text-gray-400">Cutting Size</p>
                        <p className="font-bold text-white">{workOrder.processes.process2a.cutting_size}</p>
                      </div>
                    )}
                    {workOrder.processes?.process2a?.sheet_counting && (
                      <div className="bg-cyan-500/10 p-2 rounded">
                        <p className="text-xs text-gray-400">Sheet Counting</p>
                        <p className="font-bold text-white">{workOrder.processes.process2a.sheet_counting}</p>
                      </div>
                    )}
                  </div>
                  {/* Stock Reels for P2A */}
                  {workOrder.processes?.process2a?.reels?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-400 mb-2">Stock Reels:</p>
                      <div className="flex flex-wrap gap-2">
                        {workOrder.processes.process2a.reels.map((reel, idx) => (
                          <div key={`p2a-${reel.reel_no || idx}`} className="bg-cyan-500/10 px-3 py-1 rounded text-sm">
                            <span className="text-cyan-400 font-mono">{reel.reel_no}</span>
                            {reel.reel_weight && <span className="text-gray-400 ml-2">{reel.reel_weight} Kg</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Process 2B - Paper Cutting */}
            {hasProcessData('process2b') && (
              <Card className="bg-black/60 border-l-4 border-l-sky-500 border-r border-t border-b border-gray-700">
                <CardHeader className="py-3">
                  <CardTitle className="text-sky-400 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="bg-sky-500 text-black px-2 py-0.5 rounded text-sm font-bold">P2B</span>
                      Paper Cutting Machine
                    </span>
                    <span className="text-sm text-gray-400 flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {workOrder.processes?.process2b?.date || '-'}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-3 gap-3">
                    {workOrder.processes?.process2b?.cutting_size && (
                      <div className="bg-sky-500/10 p-2 rounded">
                        <p className="text-xs text-gray-400">Cutting Size</p>
                        <p className="font-bold text-white">{workOrder.processes.process2b.cutting_size}</p>
                      </div>
                    )}
                    {workOrder.processes?.process2b?.paper_counting && (
                      <div className="bg-sky-500/10 p-2 rounded">
                        <p className="text-xs text-gray-400">Paper Counting</p>
                        <p className="font-bold text-white">{workOrder.processes.process2b.paper_counting}</p>
                      </div>
                    )}
                  </div>
                  {/* Stock Reels for P2B */}
                  {workOrder.processes?.process2b?.reels?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-400 mb-2">Stock Reels:</p>
                      <div className="flex flex-wrap gap-2">
                        {workOrder.processes.process2b.reels.map((reel, idx) => (
                          <div key={idx} className="bg-sky-500/10 px-3 py-1 rounded text-sm">
                            <span className="text-sky-400 font-mono">{reel.reel_no}</span>
                            {reel.reel_weight && <span className="text-gray-400 ml-2">{reel.reel_weight} Kg</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Process 3 */}
            {hasProcessData('process3') && (
              <Card className="bg-black/60 border-l-4 border-l-green-500 border-r border-t border-b border-gray-700">
                <CardHeader className="py-3">
                  <CardTitle className="text-green-400 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="bg-green-500 text-black px-2 py-0.5 rounded text-sm font-bold">P3</span>
                      Sheet Pasting
                    </span>
                    <span className="text-sm text-gray-400 flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {workOrder.processes?.process3?.date || '-'}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-3 gap-3">
                    {['no_of_ply', 'top_paper_type', 'sheet_counting'].map(field => {
                      const val = workOrder.processes?.process3?.[field];
                      if (!val) return null;
                      return (
                        <div key={field} className="bg-green-500/10 p-2 rounded">
                          <p className="text-xs text-gray-400 capitalize">{field.replace(/_/g, ' ')}</p>
                          <p className="font-bold text-white">{val}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Process 4-12 */}
            {[4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => {
              const key = `process${num}`;
              if (!hasProcessData(key)) return null;
              const process = workOrder.processes?.[key];
              const colors = processColors[key];
              const fields = processFields[key] || [];
              const fieldsWithValues = fields.filter(f => process?.[f]);

              return (
                <Card key={key} className={`bg-black/60 border-l-4 ${colors?.border} border-r border-t border-b border-gray-700`}>
                  <CardHeader className="py-3">
                    <CardTitle className={`${colors?.text} flex items-center justify-between`}>
                      <span className="flex items-center gap-2">
                        <span className={`${colors?.badge} text-black px-2 py-0.5 rounded text-sm font-bold`}>P{num}</span>
                        {processLabels[key]?.replace(`P${num} - `, '')}
                      </span>
                      <span className="text-sm text-gray-400 flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {process?.date || '-'}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  {fieldsWithValues.length > 0 && (
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-3 gap-3">
                        {fieldsWithValues.map(field => (
                          <div key={field} className={`${colors?.bg} p-2 rounded`}>
                            <p className="text-xs text-gray-400">{fieldLabels[field]}</p>
                            <p className="font-bold text-white">{process[field]}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}

            {/* Completion History */}
            {workOrder.completion_history?.length > 0 && (
              <Card className="bg-black/60 border-2 border-green-500/50">
                <CardHeader className="py-3">
                  <CardTitle className="text-green-400 flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Completion History
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {workOrder.completion_history.map((history, index) => (
                      <div key={`hist-${history.process_name}-${history.employee_id || index}`} className="p-3 bg-green-500/10 border border-green-500/30 rounded flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="bg-green-500 text-black px-2 py-0.5 rounded text-sm font-bold">{history.process_name?.toUpperCase()}</span>
                          <div>
                            <p className="text-white font-semibold">{history.employee_name || 'N/A'}</p>
                            <p className="text-xs text-gray-400">ID: {history.employee_id || 'N/A'}</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-400">{history.completed_at ? new Date(history.completed_at).toLocaleString() : 'N/A'}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* PRINT LAYOUT - Clean Production Floor Version - STANDARD DATA ONLY */}
        <div className="print-only">
          <style type="text/css">{`
            @media print {
              @page { size: A4; margin: 10mm; }
              body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
            .wo-print-page, .wo-print-page * { color: #000 !important; font-family: Arial, sans-serif !important; }
            .wo-print-page { background: #fff !important; padding: 15px !important; }
            .wo-print-page table { color: #000 !important; }
            .wo-print-page th, .wo-print-page td { color: #000 !important; }
            .wo-print-header { text-align: center !important; border-bottom: 2px solid #000 !important; padding-bottom: 15px !important; margin-bottom: 15px !important; }
            .wo-print-header h1 { font-size: 22px !important; font-weight: bold !important; margin: 0 !important; text-transform: uppercase !important; letter-spacing: 1px !important; color: #000 !important; }
            .wo-print-header h2 { font-size: 16px !important; color: #000 !important; margin: 5px 0 0 0 !important; }
            .wo-print-main-info { display: flex !important; justify-content: space-between !important; background: #fff !important; padding: 12px !important; margin-bottom: 15px !important; }
            .wo-print-main-info .wo-number { font-size: 28px !important; font-weight: bold !important; color: #000 !important; }
            .wo-print-main-info .wo-details { text-align: right !important; }
            .wo-print-main-info .wo-details div { font-size: 12px !important; margin: 2px 0 !important; color: #000 !important; }
            .wo-print-box-info { display: grid !important; grid-template-columns: repeat(4, 1fr) !important; gap: 10px !important; margin-bottom: 15px !important; }
            .wo-print-box-item { background: #fff !important; padding: 10px !important; text-align: center !important; border-bottom: 1px solid #ccc !important; }
            .wo-print-box-item .label { font-size: 10px !important; color: #000 !important; text-transform: uppercase !important; font-weight: bold !important; }
            .wo-print-box-item .value { font-size: 18px !important; font-weight: bold !important; color: #000 !important; margin-top: 3px !important; }
            .wo-print-section { margin-bottom: 12px !important; page-break-inside: avoid !important; }
            .wo-print-section-header { background: #f0f0f0 !important; color: #000 !important; padding: 8px 12px !important; font-size: 13px !important; font-weight: bold !important; display: flex !important; justify-content: space-between !important; }
            .wo-print-section-header span { color: #000 !important; }
            .wo-print-section-header .process-date { font-size: 11px !important; font-weight: normal !important; background: #f0f0f0 !important; color: #000 !important; padding: 2px 8px !important; }
            .wo-print-task-table { width: 100% !important; border-collapse: collapse !important; }
            .wo-print-task-table th { background: #f0f0f0 !important; padding: 8px 10px !important; text-align: left !important; font-size: 11px !important; font-weight: bold !important; border-bottom: 1px solid #ccc !important; color: #000 !important; }
            .wo-print-task-table td { padding: 8px 10px !important; font-size: 12px !important; border-bottom: 1px solid #eee !important; color: #000 !important; background: #fff !important; }
            .wo-print-task-table .task-label { font-weight: bold !important; background: #fafafa !important; width: 35% !important; color: #000 !important; }
            .wo-print-task-table .task-value { font-size: 14px !important; color: #000 !important; font-weight: bold !important; }
            .wo-print-task-table .highlight-row { background: #f5f5f5 !important; }
            .wo-print-task-table .highlight-row td { color: #000 !important; }
            .wo-print-materials table { width: 100% !important; border-collapse: collapse !important; margin-top: 5px !important; }
            .wo-print-materials th { background: #f0f0f0 !important; padding: 6px 8px !important; font-size: 10px !important; border-bottom: 1px solid #ccc !important; text-align: center !important; font-weight: bold !important; color: #000 !important; }
            .wo-print-materials td { padding: 6px 8px !important; font-size: 11px !important; border-bottom: 1px solid #eee !important; text-align: center !important; color: #000 !important; background: #fff !important; }
            .wo-print-footer { margin-top: 20px !important; display: flex !important; justify-content: space-between !important; border-top: 1px solid #ccc !important; padding-top: 15px !important; }
            .wo-print-footer .signature-box { width: 45% !important; }
            .wo-print-footer .signature-label { font-size: 11px !important; color: #000 !important; margin-bottom: 5px !important; font-weight: bold !important; }
            .wo-print-footer .signature-line { border-bottom: 1px solid #999 !important; height: 40px !important; }
            .wo-print-qty-badge { display: inline-block !important; background: #000 !important; color: #fff !important; padding: 5px 15px !important; font-size: 16px !important; font-weight: bold !important; }
            .wo-print-size-box { flex: 1 !important; background: #fff !important; padding: 10px !important; text-align: center !important; border-bottom: 1px solid #ccc !important; }
            .wo-print-size-label { font-size: 10px !important; color: #000 !important; text-transform: uppercase !important; font-weight: bold !important; }
            .wo-print-size-value { font-size: 18px !important; font-weight: bold !important; color: #000 !important; }
            .wo-print-product-qr { text-align: center !important; margin-top: 25px !important; padding-top: 15px !important; border-top: 1px solid #ccc !important; page-break-inside: avoid !important; }
            .wo-print-product-qr-label { font-size: 12px !important; font-weight: bold !important; color: #000 !important; margin-bottom: 10px !important; text-transform: uppercase !important; }
            .wo-print-product-qr-row { display: flex !important; flex-direction: row !important; flex-wrap: wrap !important; justify-content: center !important; gap: 16px !important; }
            .wo-print-product-qr-item { display: inline-flex !important; flex-direction: column !important; align-items: center !important; padding: 6px 8px !important; border: 1px solid #ddd !important; border-radius: 4px !important; background: #fff !important; }
            .wo-print-product-qr-img { width: 110px !important; height: 110px !important; display: block !important; }
            .wo-print-product-qr-caption { font-size: 9px !important; color: #000 !important; margin-top: 4px !important; font-weight: bold !important; max-width: 110px !important; overflow: hidden !important; text-overflow: ellipsis !important; white-space: nowrap !important; }
            .wo-print-product-qr-hint { font-size: 9px !important; color: #666 !important; margin-top: 8px !important; }
          `}</style>
          
          <div className="wo-print-page">
            {/* Header */}
            <div className="wo-print-header">
              <h1>Challenge Packaging Industries</h1>
              <h2>WORK ORDER - PRODUCTION SHEET</h2>
            </div>

            {/* Main Info */}
            <div className="wo-print-main-info">
              <div>
                <div className="wo-number">{workOrder.header?.work_order_number || '-'}</div>
                <div style={{fontSize: '14px', marginTop: '5px'}}><strong>Customer:</strong> {workOrder.header?.customer_name || '-'}</div>
                <div style={{fontSize: '14px'}}><strong>Item:</strong> {workOrder.header?.item_name || '-'}</div>
              </div>
              <div className="wo-details">
                <div><strong>Date:</strong> {workOrder.header?.work_order_creation_date || '-'}</div>
                <div><strong>Delivery:</strong> {workOrder.header?.delivery_date || '-'}</div>
                <div style={{marginTop: '8px'}}>
                  <span className="wo-print-qty-badge">QTY: {workOrder.header?.required_quantity || '-'}</span>
                </div>
              </div>
            </div>

            {/* Box Dimensions */}
            <div className="wo-print-box-info">
              <div className="wo-print-box-item">
                <div className="label">Length</div>
                <div className="value">{workOrder.header?.length_mm || '-'} mm</div>
              </div>
              <div className="wo-print-box-item">
                <div className="label">Breadth</div>
                <div className="value">{workOrder.header?.breadth_mm || '-'} mm</div>
              </div>
              <div className="wo-print-box-item">
                <div className="label">Height</div>
                <div className="value">{workOrder.header?.height_mm || '-'} mm</div>
              </div>
              <div className="wo-print-box-item">
                <div className="label">PLY</div>
                <div className="value">{workOrder.processes?.process3?.no_of_ply || '-'}</div>
              </div>
            </div>

            {/* Reel & Cutting Size */}
            <div style={{display: 'flex', gap: '10px', marginBottom: '15px'}}>
              <div className="wo-print-size-box">
                <div className="wo-print-size-label">Reel Size</div>
                <div className="wo-print-size-value">{workOrder.header?.reel_size || '-'}"</div>
              </div>
              <div className="wo-print-size-box">
                <div className="wo-print-size-label">Cutting Size</div>
                <div className="wo-print-size-value">{workOrder.header?.cutting_size || '-'}"</div>
              </div>
            </div>

            {/* Process 1 - Corrugation */}
            {hasProcessData('process1') && workOrder.processes?.process1 && (
              <div className="wo-print-section">
                <div className="wo-print-section-header">
                  <span>P1 - CORRUGATION</span>
                  <span className="process-date">Date: {workOrder.processes.process1.date || '-'}</span>
                </div>
                <table className="wo-print-task-table">
                  <tbody>
                    <tr className="highlight-row">
                      <td className="task-label" style={{color: '#000'}}>Flute Type</td>
                      <td className="task-value" style={{color: '#000'}}>{workOrder.processes.process1.flute_type || '-'}</td>
                    </tr>
                    {(workOrder.processes.process1.rows || []).filter(r => r.base_reel_id || r.flute_reel_id).map((row, i) => (
                      <tr key={`p1-row-${i}`}>
                        <td className="task-label" style={{color: '#000'}}>Base: {row.base_reel_id || '-'} ({row.base_weight || '-'}kg)</td>
                        <td className="task-value" style={{color: '#000'}}>Flute: {row.flute_reel_id || '-'} ({row.flute_weight || '-'}kg)</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Process 2 (Legacy) - Sheet/Paper Cutting */}
            {hasProcessData('process2') && workOrder.processes?.process2 && (
              <div className="wo-print-section">
                <div className="wo-print-section-header">
                  <span>P2 - SHEET/PAPER CUTTING (LEGACY)</span>
                  <span className="process-date">Date: {workOrder.processes.process2.date || '-'}</span>
                </div>
                <table className="wo-print-task-table">
                  <tbody>
                    <tr><td className="task-label" style={{color: '#000'}}>Cutting Size</td><td className="task-value" style={{color: '#000'}}>{workOrder.processes.process2.cutting_size || '-'}</td></tr>
                    <tr><td className="task-label" style={{color: '#000'}}>Sheet Count</td><td className="task-value" style={{color: '#000'}}>{workOrder.processes.process2.sheet_counting || '-'}</td></tr>
                    <tr><td className="task-label" style={{color: '#000'}}>Paper Count</td><td className="task-value" style={{color: '#000'}}>{workOrder.processes.process2.paper_counting || '-'}</td></tr>
                    {(workOrder.processes.process2.reels || []).filter(r => r.reel_no).map((reel, i) => (
                      <tr key={`p2-reel-${i}`}>
                        <td className="task-label" style={{color: '#000'}}>Reel: {reel.reel_no} ({reel.reel_size || '-'}")</td>
                        <td className="task-value" style={{color: '#000'}}>{reel.reel_weight || '-'}kg - {reel.reel_product || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Process 2A - Sheet Cutting */}
            {hasProcessData('process2a') && workOrder.processes?.process2a && (
              <div className="wo-print-section">
                <div className="wo-print-section-header">
                  <span>P2A - SHEET CUTTING</span>
                  <span className="process-date">Date: {workOrder.processes.process2a.date || '-'}</span>
                </div>
                <table className="wo-print-task-table">
                  <tbody>
                    <tr><td className="task-label" style={{color: '#000'}}>Reel Size</td><td className="task-value" style={{color: '#000'}}>{workOrder.processes.process2a.reel_size || '-'}</td></tr>
                    <tr><td className="task-label" style={{color: '#000'}}>Cutting Size</td><td className="task-value" style={{color: '#000'}}>{workOrder.processes.process2a.cutting_size || '-'}</td></tr>
                    <tr className="highlight-row"><td className="task-label" style={{color: '#000'}}>Sheet Counting</td><td className="task-value" style={{color: '#000'}}>{workOrder.processes.process2a.sheet_counting || '-'}</td></tr>
                    {(workOrder.processes.process2a.reels || []).filter(r => r.reel_no).map((reel, i) => (
                      <tr key={`p2a-reel-${i}`}>
                        <td className="task-label" style={{color: '#000'}}>Reel: {reel.reel_no}</td>
                        <td className="task-value" style={{color: '#000'}}>{reel.reel_weight || '-'} Kg</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Process 2B - Paper Cutting */}
            {hasProcessData('process2b') && workOrder.processes?.process2b && (
              <div className="wo-print-section">
                <div className="wo-print-section-header">
                  <span>P2B - PAPER CUTTING</span>
                  <span className="process-date">Date: {workOrder.processes.process2b.date || '-'}</span>
                </div>
                <table className="wo-print-task-table">
                  <tbody>
                    <tr><td className="task-label" style={{color: '#000'}}>Cutting Size</td><td className="task-value" style={{color: '#000'}}>{workOrder.processes.process2b.cutting_size || '-'}</td></tr>
                    <tr className="highlight-row"><td className="task-label" style={{color: '#000'}}>Paper Counting</td><td className="task-value" style={{color: '#000'}}>{workOrder.processes.process2b.paper_counting || '-'}</td></tr>
                    {(workOrder.processes.process2b.reels || []).filter(r => r.reel_no).map((reel, i) => (
                      <tr key={`p2b-reel-${i}`}>
                        <td className="task-label" style={{color: '#000'}}>Reel: {reel.reel_no}</td>
                        <td className="task-value" style={{color: '#000'}}>{reel.reel_weight || '-'} Kg</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Process 3 - Sheet Pasting */}
            {hasProcessData('process3') && workOrder.processes?.process3 && (
              <div className="wo-print-section">
                <div className="wo-print-section-header">
                  <span>P3 - SHEET PASTING</span>
                  <span className="process-date">Date: {workOrder.processes.process3.date || '-'}</span>
                </div>
                <table className="wo-print-task-table">
                  <tbody>
                    <tr className="highlight-row"><td className="task-label" style={{color: '#000'}}>PLY</td><td className="task-value" style={{color: '#000'}}>{workOrder.processes.process3.no_of_ply || '-'}</td></tr>
                    <tr><td className="task-label" style={{color: '#000'}}>Top Paper Type</td><td className="task-value" style={{color: '#000'}}>{workOrder.processes.process3.top_paper_type || '-'}</td></tr>
                    <tr><td className="task-label" style={{color: '#000'}}>Sheet Counting</td><td className="task-value" style={{color: '#000'}}>{workOrder.processes.process3.sheet_counting || '-'}</td></tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Process 4 */}
            {hasProcessData('process4') && (
              <div className="wo-print-section">
                <div className="wo-print-section-header">
                  <span>P4 - SLITTER SCORER</span>
                  <span className="process-date">Date: {workOrder.processes?.process4?.date || '-'}</span>
                </div>
                <table className="wo-print-task-table">
                  <tbody>
                    <tr className="highlight-row"><td className="task-label">Board Counting</td><td className="task-value">{workOrder.processes?.process4?.board_counting || '-'}</td></tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Process 5 */}
            {hasProcessData('process5') && (
              <div className="wo-print-section">
                <div className="wo-print-section-header">
                  <span>P5 - ROTARY CREASING</span>
                  <span className="process-date">Date: {workOrder.processes?.process5?.date || '-'}</span>
                </div>
                <table className="wo-print-task-table">
                  <tbody>
                    <tr><td className="task-label">Printing</td><td className="task-value">{workOrder.processes?.process5?.printing || '-'}</td></tr>
                    <tr><td className="task-label">Color</td><td className="task-value">{workOrder.processes?.process5?.color || '-'}</td></tr>
                    <tr className="highlight-row"><td className="task-label">Board Counting</td><td className="task-value">{workOrder.processes?.process5?.board_counting || '-'}</td></tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Process 6 */}
            {hasProcessData('process6') && (
              <div className="wo-print-section">
                <div className="wo-print-section-header">
                  <span>P6 - RS4 PRINTING</span>
                  <span className="process-date">Date: {workOrder.processes?.process6?.date || '-'}</span>
                </div>
                <table className="wo-print-task-table">
                  <tbody>
                    <tr><td className="task-label">Printing</td><td className="task-value">{workOrder.processes?.process6?.printing || '-'}</td></tr>
                    <tr><td className="task-label">Color</td><td className="task-value">{workOrder.processes?.process6?.color || '-'}</td></tr>
                    <tr className="highlight-row"><td className="task-label">Board Counting</td><td className="task-value">{workOrder.processes?.process6?.board_counting || '-'}</td></tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Process 7 */}
            {hasProcessData('process7') && (
              <div className="wo-print-section">
                <div className="wo-print-section-header">
                  <span>P7 - FLAT BED PUNCHING</span>
                  <span className="process-date">Date: {workOrder.processes?.process7?.date || '-'}</span>
                </div>
                <table className="wo-print-task-table">
                  <tbody>
                    <tr className="highlight-row"><td className="task-label">Die ID</td><td className="task-value">{workOrder.processes?.process7?.die_id || '-'}</td></tr>
                    <tr><td className="task-label">Counting</td><td className="task-value">{workOrder.processes?.process7?.counting || '-'}</td></tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Process 8 */}
            {hasProcessData('process8') && (
              <div className="wo-print-section">
                <div className="wo-print-section-header">
                  <span>P8 - ROTARY DIE PUNCHING</span>
                  <span className="process-date">Date: {workOrder.processes?.process8?.date || '-'}</span>
                </div>
                <table className="wo-print-task-table">
                  <tbody>
                    <tr className="highlight-row"><td className="task-label">Die ID</td><td className="task-value">{workOrder.processes?.process8?.die_id || '-'}</td></tr>
                    <tr><td className="task-label">Printing</td><td className="task-value">{workOrder.processes?.process8?.printing || '-'}</td></tr>
                    <tr><td className="task-label">Color</td><td className="task-value">{workOrder.processes?.process8?.color || '-'}</td></tr>
                    <tr><td className="task-label">Counting</td><td className="task-value">{workOrder.processes?.process8?.counting || '-'}</td></tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Process 9 */}
            {hasProcessData('process9') && (
              <div className="wo-print-section">
                <div className="wo-print-section-header">
                  <span>P9 - CALICO PASTING</span>
                  <span className="process-date">Date: {workOrder.processes?.process9?.date || '-'}</span>
                </div>
                <table className="wo-print-task-table">
                  <tbody>
                    <tr><td className="task-label">Calico Color</td><td className="task-value">{workOrder.processes?.process9?.calico_color || '-'}</td></tr>
                    <tr><td className="task-label">Number of Calico</td><td className="task-value">{workOrder.processes?.process9?.number_of_calico || '-'}</td></tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Process 10 */}
            {hasProcessData('process10') && (
              <div className="wo-print-section">
                <div className="wo-print-section-header">
                  <span>P10 - STITCHING & BUNDLING</span>
                  <span className="process-date">Date: {workOrder.processes?.process10?.date || '-'}</span>
                </div>
                <table className="wo-print-task-table">
                  <tbody>
                    <tr><td className="task-label">Boxes in Bundle</td><td className="task-value">{workOrder.processes?.process10?.boxes_in_bundle || '-'}</td></tr>
                    <tr><td className="task-label">Stitching Type</td><td className="task-value">{workOrder.processes?.process10?.stitching_type || '-'}</td></tr>
                    <tr className="highlight-row"><td className="task-label">Total Bundles</td><td className="task-value">{workOrder.processes?.process10?.total_bundles || '-'}</td></tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer */}
            <div className="wo-print-footer">
              <div className="signature-box">
                <div className="signature-label">Prepared By</div>
                <div className="signature-line"></div>
              </div>
              <div className="signature-box">
                <div className="signature-label">Approved By</div>
                <div className="signature-line"></div>
              </div>
            </div>

            {/* Item Image QR Codes — one per image, laid out in a row */}
            {productImages.length > 0 && (
              <div className="wo-print-product-qr" data-testid="wo-product-qr-section">
                <div className="wo-print-product-qr-label">
                  Item Image{productImages.length > 1 ? "s" : ""} ({productImages.length})
                </div>
                <div className="wo-print-product-qr-row">
                  {productImages.map((img, idx) => (
                    <div
                      key={img.url}
                      className="wo-print-product-qr-item"
                      data-testid={`wo-product-qr-${idx}`}
                    >
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(img.url)}&ecc=M`}
                        alt={`Item Image QR ${idx + 1}`}
                        className="wo-print-product-qr-img"
                      />
                      <div className="wo-print-product-qr-caption">
                        {img.filename || `Image ${idx + 1}`}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="wo-print-product-qr-hint">Scan any QR to view item image</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewWorkOrder;
